/**
 * EnhancedDrawer Component
 *
 * Note: for push mode, you will have to add a global class 'enhanced-drawer-pushable' to the parent
 * element that contains both the EnhancedDrawer and the main content area to be pushed.
 * This is necessary to apply the CSS variable for push width to the correct container.
 */
import React, {
  FC,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react'
import classnames from 'classnames/bind'
import { createPortal } from 'react-dom'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import styles from './styles.scss'
import type { AutomationTarget } from '../../../types'
import { useBreakpoints, useWindowDimensions } from '../../../hooks'

// Minimum content width before switching from push to overlay in responsive mode
const MIN_PUSH_WIDTH = 992 // laptop or greater

const cx = classnames.bind(styles)

export type EnhancedDrawerMode = 'sidebar' | 'floating' | 'fullscreen'
export type EnhancedDrawerPushBehavior = 'overlay' | 'push' | 'responsive'
export type EnhancedDrawerMobileLayout = 'fullscreen' | 'right-panel'

export interface EnhancedDrawerProps extends AutomationTarget {
  open: boolean
  /**
   * Desktop presentation:
   * - `sidebar` (default): docked right, resizable.
   * - `floating`: detached rounded card, bottom-right.
   * - `fullscreen`: docked full-viewport-width panel (edge to edge). The resize
   *   handle and mode toggle are hidden since neither applies. Handy for hosting
   *   a full page-width experience in a slide-over that still layers below app
   *   modals (modal-over-drawer is a supported stack, unlike modal-over-modal).
   *
   * Mobile layout is governed separately by `mobileLayout`.
   */
  mode?: EnhancedDrawerMode
  onClose: () => void
  onModeChange?: (mode: EnhancedDrawerMode) => void
  sidebarWidth?: number
  floatingWidth?: number
  floatingHeight?: number
  children: ReactNode
  headerContent?: ReactNode
  /**
   * Pinned footer content (e.g. an action bar). Rendered below the scrollable
   * content area and stays fixed while the body scrolls — mirrors the footer
   * model of the `ui` Drawer. Omit to render no footer.
   */
  footerContent?: ReactNode
  className?: string
  onWidthChange?: (width: number) => void
  minWidth?: number
  maxWidth?: number
  persistWidth?: boolean
  keepMounted?: boolean
  topMarginOnDesktop?: number
  style?: React.CSSProperties
  pushBehavior?: EnhancedDrawerPushBehavior
  minPushWidth?: number
  onPushWidthChange?: (width: number) => void
  mobileLayout?: EnhancedDrawerMobileLayout
  mobileMaxWidth?: number
  closeOnBackdropClick?: boolean
  /** Container element for the portal. Defaults to document.body. Use to render inside a shadow root. */
  portalContainer?: Element | DocumentFragment
}

const EnhancedDrawer: FC<EnhancedDrawerProps> = ({
  open,
  mode: controlledMode,
  onClose,
  onModeChange,
  sidebarWidth = 330,
  floatingWidth = 500,
  floatingHeight = 400,
  children,
  headerContent,
  footerContent,
  className = null,
  onWidthChange,
  minWidth = 300,
  maxWidth,
  persistWidth = true,
  keepMounted = true,
  topMarginOnDesktop,
  elmId = 'enhanced-drawer',
  style,
  pushBehavior = 'responsive',
  minPushWidth = MIN_PUSH_WIDTH,
  onPushWidthChange,
  mobileLayout = 'fullscreen',
  mobileMaxWidth = 350,
  closeOnBackdropClick = true,
  portalContainer,
}) => {
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  )
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const breakpoint = useBreakpoints()
  const { width: viewportWidth } = useWindowDimensions()

  // Internal state for uncontrolled mode
  const [internalMode, setInternalMode] =
    useState<EnhancedDrawerMode>('sidebar')

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = controlledMode !== undefined
  const mode = isControlled ? controlledMode : internalMode

  // Initialize width from localStorage or prop
  const [currentWidth, setCurrentWidth] = useState<number>(() => {
    if (typeof window === 'undefined' || !persistWidth) return sidebarWidth
    const savedWidth = localStorage.getItem(
      `enhancedDrawer.sidebarWidth.${elmId}`
    )
    return savedWidth ? parseInt(savedWidth, 10) : sidebarWidth
  })
  const [isAnimating, setIsAnimating] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const mountedRef = useRef(true)

  // Clean up on unmount.
  // NOTE: must (re)set true in the effect body, not only false in cleanup.
  // React 18 StrictMode double-invokes effects (setup -> cleanup -> setup); a
  // cleanup-only reset leaves mountedRef.current === false after the double
  // invoke, which makes the open/visibility guards below (if (mountedRef.current)
  // setVisible(true)) no-op -> the drawer mounts but never becomes visible.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Determine if we should be pushing content
  const shouldPush = useMemo(() => {
    if (pushBehavior === 'overlay') return false
    if (mode === 'floating' || mode === 'fullscreen') return false
    /**
     * For 'push' mode, if 'responsive' is selected, only push when content is
     * greater than minPushWidth. This prevents squishing content on smaller viewports.
     */
    if (pushBehavior === 'responsive') {
      const remainingContentWidth = viewportWidth - currentWidth
      if (remainingContentWidth < minPushWidth) {
        return false
      }
    }
    return true
  }, [pushBehavior, mode, viewportWidth, currentWidth, minPushWidth])

  // Calculate push width - push when open and conditions are met
  const pushWidth = open && shouldPush ? currentWidth : 0

  // Manage CSS variable for push behavior
  useEffect(() => {
    if (pushBehavior === 'overlay') return
    if (typeof document === 'undefined') return

    const cssVarName = '--enhanced-drawer-push-width'

    document.documentElement.style.setProperty(cssVarName, `${pushWidth}px`)
    onPushWidthChange?.(pushWidth)

    return () => {
      document.documentElement.style.setProperty(cssVarName, '0px')
    }
  }, [pushWidth, pushBehavior, elmId, onPushWidthChange])

  // Handle mounting and visibility for animations
  useEffect(() => {
    if (open && !mounted) {
      // Initial mount
      setMounted(true)
      setIsAnimating(true)
      // Double rAF: a single rAF fires before the browser paints the just-
      // inserted off-screen state (translateX/Y(100%)), so flipping to `.open`
      // in one frame skips the enter transition (drawer appears instantly on
      // its FIRST open after mount). Waiting two frames guarantees the initial
      // off-screen frame is painted, so the transition to `.open` animates.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mountedRef.current) {
            setVisible(true)
            // Animation complete after transition duration
            setTimeout(() => {
              if (mountedRef.current) {
                setIsAnimating(false)
              }
            }, 300)
          }
        })
      })
    } else if (open && mounted && !visible) {
      // Reopening when keepMounted is true
      setIsAnimating(true)
      setIsClosing(false)
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        if (mountedRef.current) {
          setVisible(true)
          setTimeout(() => {
            if (mountedRef.current) {
              setIsAnimating(false)
            }
          }, 300)
        }
      })
    } else if (!open && mounted) {
      // Closing - keep visible true to show closing animation
      setIsAnimating(true)
      setIsClosing(true)
      // Don't set visible to false immediately - let the closing animation play
      // After animation completes, hide and clean up
      setTimeout(() => {
        if (mountedRef.current) {
          setVisible(false)
          // Only unmount if keepMounted is false
          if (!keepMounted) {
            setMounted(false)
          }
          setIsAnimating(false)
          setIsClosing(false)
        }
      }, 300)
    }
  }, [open, mounted, visible, keepMounted])

  // Update viewport height on resize
  useEffect(() => {
    const handleResize = () => {
      if (mountedRef.current) {
        setViewportHeight(window.innerHeight)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleModeToggle = useCallback(() => {
    const newMode = mode === 'sidebar' ? 'floating' : 'sidebar'

    // If uncontrolled, update internal state
    if (!isControlled) {
      setInternalMode(newMode)
    }

    // Always call onModeChange if provided
    onModeChange?.(newMode)
  }, [mode, onModeChange, isControlled])

  const handleClose = useCallback(() => {
    if (!isAnimating) {
      onClose()
    }
  }, [onClose, isAnimating])

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdropClick && !isAnimating) {
      onClose()
    }
  }, [closeOnBackdropClick, onClose, isAnimating])

  // Calculate top position for floating mode to maintain bottom position
  const floatingTop = viewportHeight - floatingHeight - 12 // 12px for bottom margin

  // Determine if we should apply the top margin
  const shouldApplyTopMargin =
    mode === 'sidebar' && breakpoint !== 'mobile' && topMarginOnDesktop

  const shouldShowBackdrop =
    breakpoint === 'mobile' && mobileLayout === 'right-panel' && visible

  const drawerStyle: React.CSSProperties =
    // eslint-disable-next-line no-nested-ternary
    mode === 'sidebar'
      ? {
          width: `${currentWidth}px`,
          height: shouldApplyTopMargin
            ? `${viewportHeight - topMarginOnDesktop}px`
            : `${viewportHeight}px`,
          top: shouldApplyTopMargin ? `${topMarginOnDesktop}px` : '0px',
          bottom: 'auto',
        }
      : mode === 'fullscreen'
      ? {
          // No inline width: the .mode-fullscreen CSS pins left:0/right:0 so the
          // panel stretches edge to edge without the 100vw scrollbar-overflow
          // gotcha (100vw includes the vertical scrollbar gutter).
          height: `${viewportHeight}px`,
          top: '0px',
          bottom: 'auto',
        }
      : mode === 'floating'
      ? {
          width: `${floatingWidth}px`,
          height: `${floatingHeight}px`,
          top: `${floatingTop}px`,
          bottom: 'auto',
        }
      : {}

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = window.innerWidth - e.clientX
      const effectiveMaxWidth = maxWidth || window.innerWidth * 0.8

      if (newWidth >= minWidth && newWidth <= effectiveMaxWidth) {
        setCurrentWidth(newWidth)
        if (onWidthChange) {
          onWidthChange(newWidth)
        }
      }
    },
    [isResizing, minWidth, maxWidth, onWidthChange]
  )

  const handleResizeEnd = useCallback(() => {
    if (!isResizing) return

    setIsResizing(false)

    // Save to localStorage
    if (persistWidth) {
      localStorage.setItem(
        `enhancedDrawer.sidebarWidth.${elmId}`,
        currentWidth.toString()
      )
    }
  }, [isResizing, persistWidth, elmId, currentWidth])

  // Add resize event listeners
  // @ts-ignore next
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      // Prevent text selection during resize
      document.body.style.userSelect = 'none'

      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
        document.body.style.userSelect = ''
      }
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  if (!mounted) return null

  const drawerContent = (
    <>
      {shouldShowBackdrop && (
        <div
          className={cx(
            'backdrop',
            visible && !isClosing && 'visible',
            isClosing && 'closing'
          )}
          onClick={handleBackdropClick}
          data-elm-id={`${elmId}-backdrop`}
          aria-hidden='true'
        />
      )}
      <div
        className={cx(
          'enhanced-drawer',
          `mode-${mode}`,
          `mobile-${mobileLayout}`,
          visible && 'open',
          isAnimating && 'animating',
          isClosing && 'closing',
          isResizing && 'resizing',
          keepMounted && mounted && !visible && 'hidden',
          className
        )}
        style={
          {
            ...drawerStyle,
            ...style,
            '--mobile-max-width': `${mobileMaxWidth}px`,
          } as React.CSSProperties
        }
        data-elm-id={elmId}
      >
        {mode === 'sidebar' && (
          <div
            className={cx('resize-handle', isResizing && 'resizing')}
            onMouseDown={handleResizeStart}
            data-elm-id={`${elmId}-resize-handle`}
            aria-label='Resize drawer'
            role='separator'
            aria-orientation='vertical'
          />
        )}
        <div className={cx('header')} data-elm-id={`${elmId}-header`}>
          <div className={cx('header-left')}>{headerContent}</div>
          <div className={cx('header-right')}>
            {mode !== 'fullscreen' && (
              <Button
                role='tertiary'
                icon={
                  <Icon
                    name={mode === 'sidebar' ? 'window' : 'sidebar-flip'}
                    iconStyle='regular'
                    color='gray'
                  />
                }
                onClick={handleModeToggle}
                size='medium'
                elmId={`${elmId}-mode-toggle`}
                collapse
              />
            )}
            <Button
              role='tertiary'
              icon={<Icon name='times' iconStyle='regular' color='gray' />}
              onClick={handleClose}
              size='medium'
              elmId={`${elmId}-close`}
              collapse
            />
          </div>
        </div>
        <div className={cx('content')} data-elm-id={`${elmId}-content`}>
          {children}
        </div>
        {footerContent && (
          <div className={cx('footer')} data-elm-id={`${elmId}-footer`}>
            {footerContent}
          </div>
        )}
      </div>
    </>
  )

  // Always use portal to ensure consistent rendering and smooth transitions
  return createPortal(drawerContent, portalContainer || document.body)
}

export default EnhancedDrawer
